import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ArrowLeft, Mail, Phone, MapPin, Calendar, Clock, Shield, User, Briefcase, Heart, Send, CheckCircle, FileText, RefreshCw, LogIn, ClipboardList, Plus, Upload, Download, Trash2, File, FileSpreadsheet, FileType, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ['Pending', 'Approved', 'Active', 'Inactive', 'Rejected'];
const BG_CHECK_OPTIONS = ['Pending', 'Completed', 'Expired'];

const EMAIL_TEMPLATES = [
  {
    id: 'application_confirmation',
    name: 'Application Confirmation',
    description: 'Resend the application confirmation email',
    icon: FileText
  },
  {
    id: 'approval',
    name: 'Approval Letter',
    description: 'Send the approval/welcome email',
    icon: CheckCircle
  },
  {
    id: 'rejection',
    name: 'Rejection Letter',
    description: 'Send rejection notification',
    icon: Mail,
    requiresReason: true
  },
  {
    id: 'custom',
    name: 'Custom Email',
    description: 'Write a custom message',
    icon: Send,
    requiresCustom: true
  }
];

export default function VolunteerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [volunteer, setVolunteer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Email state
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customSubject, setCustomSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Tasks state
  const [volunteerTasks, setVolunteerTasks] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showAssignTaskDialog, setShowAssignTaskDialog] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [taskCustomMessage, setTaskCustomMessage] = useState('');
  const [assigningTask, setAssigningTask] = useState(false);
  
  // Documents state
  const [volunteerDocuments, setVolunteerDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showUploadDocDialog, setShowUploadDocDialog] = useState(false);
  const [uploadDocTitle, setUploadDocTitle] = useState('');
  const [uploadDocDescription, setUploadDocDescription] = useState('');
  const [uploadDocFile, setUploadDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [sendDocNotification, setSendDocNotification] = useState(true);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  useEffect(() => {
    fetchVolunteer();
  }, [id]);

  const fetchVolunteer = async () => {
    try {
      const response = await fetch(`${API}/volunteers/${id}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setVolunteer(data);
        setEmailHistory(data.emails_sent || []);
        setVolunteerTasks(data.assigned_tasks || []);
        setVolunteerDocuments(data.assigned_documents || []);
      } else if (response.status === 404) {
        toast.error('Volunteer not found');
        navigate('/admin/volunteers');
      }
    } catch (error) {
      console.error('Error fetching volunteer:', error);
      toast.error('Failed to load volunteer');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailHistory = async () => {
    setLoadingEmails(true);
    try {
      const response = await fetch(`${API}/volunteers/${id}/emails`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEmailHistory(data.emails_sent || []);
      }
    } catch (error) {
      console.error('Error fetching email history:', error);
    } finally {
      setLoadingEmails(false);
    }
  };

  const updateVolunteer = async (field, value) => {
    setUpdating(true);
    try {
      const response = await fetch(`${API}/volunteers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value })
      });

      if (response.ok) {
        setVolunteer(prev => ({ ...prev, [field]: value }));
        toast.success('Volunteer updated successfully');
      } else {
        toast.error('Failed to update volunteer');
      }
    } catch (error) {
      console.error('Error updating volunteer:', error);
      toast.error('Failed to update volunteer');
    } finally {
      setUpdating(false);
    }
  };

  const deleteVolunteer = async () => {
    if (!window.confirm('Are you sure you want to delete this volunteer?')) return;

    try {
      const response = await fetch(`${API}/volunteers/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Volunteer deleted successfully');
        navigate('/admin/volunteers');
      } else {
        toast.error('Failed to delete volunteer');
      }
    } catch (error) {
      console.error('Error deleting volunteer:', error);
      toast.error('Failed to delete volunteer');
    }
  };

  const openEmailDialog = (template) => {
    setSelectedTemplate(template);
    setCustomSubject('');
    setCustomMessage('');
    setRejectionReason('');
    setShowEmailDialog(true);
  };

  const sendEmail = async () => {
    if (!selectedTemplate) return;

    // Validation
    if (selectedTemplate.id === 'custom' && (!customSubject || !customMessage)) {
      toast.error('Please enter subject and message for custom email');
      return;
    }
    if (selectedTemplate.id === 'rejection' && !rejectionReason) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setSendingEmail(true);
    try {
      const payload = {
        email_type: selectedTemplate.id,
        custom_subject: customSubject || null,
        custom_message: customMessage || null,
        rejection_reason: rejectionReason || null
      };

      const response = await fetch(`${API}/volunteers/${id}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Email sent successfully!');
        setShowEmailDialog(false);
        fetchEmailHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatEmailType = (type) => {
    const types = {
      'application_confirmation': 'Application Confirmation',
      'approval': 'Approval Letter',
      'rejection': 'Rejection Letter',
      'custom': 'Custom Email'
    };
    return types[type] || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const fetchVolunteerTasks = async () => {
    setLoadingTasks(true);
    try {
      const response = await fetch(`${API}/volunteers/${id}/tasks`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setVolunteerTasks(data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchAvailableTasks = async () => {
    try {
      // Get tasks that match volunteer's interests and aren't already assigned
      const response = await fetch(`${API}/admin/tasks?status=Open`, { credentials: 'include' });
      if (response.ok) {
        const allTasks = await response.json();
        // Filter tasks matching volunteer interests
        const matching = allTasks.filter(task => 
          volunteer?.volunteer_roles?.includes(task.related_interest) &&
          !task.assigned_volunteers?.includes(id)
        );
        setAvailableTasks(matching);
      }
    } catch (error) {
      console.error('Error fetching available tasks:', error);
    }
  };

  const openAssignTaskDialog = () => {
    fetchAvailableTasks();
    setSelectedTaskId('');
    setTaskCustomMessage('');
    setShowAssignTaskDialog(true);
  };

  const assignTaskToVolunteer = async () => {
    if (!selectedTaskId) {
      toast.error('Please select a task');
      return;
    }

    setAssigningTask(true);
    try {
      const response = await fetch(`${API}/volunteers/${id}/assign-task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          task_id: selectedTaskId,
          send_notification: true,
          custom_message: taskCustomMessage || null
        })
      });

      if (response.ok) {
        toast.success('Task assigned successfully');
        setShowAssignTaskDialog(false);
        fetchVolunteer();
        fetchVolunteerTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign task');
      }
    } catch (error) {
      toast.error('Failed to assign task');
    } finally {
      setAssigningTask(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      High: 'bg-red-100 text-red-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      Low: 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-slate-100 text-slate-800';
  };

  const getTaskStatusColor = (status) => {
    const colors = {
      Assigned: 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-purple-100 text-purple-800',
      Completed: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  // Document functions
  const fetchDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const response = await fetch(`${API}/admin/volunteers/${id}/documents`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setVolunteerDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDocFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large. Maximum size is 10MB');
      e.target.value = '';
      return;
    }

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PDF, Word, Excel, CSV');
      e.target.value = '';
      return;
    }

    setUploadDocFile(file);
    if (!uploadDocTitle) {
      setUploadDocTitle(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadDocFile || !uploadDocTitle) {
      toast.error('Please select a file and enter a title');
      return;
    }

    setUploadingDoc(true);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result.split(',')[1];

        const response = await fetch(`${API}/admin/volunteers/${id}/upload-document`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: uploadDocTitle,
            description: uploadDocDescription,
            filename: uploadDocFile.name,
            file_type: uploadDocFile.type,
            file_data: base64Data,
            file_size: uploadDocFile.size,
            send_notification: sendDocNotification
          })
        });

        if (response.ok) {
          toast.success('Document uploaded and shared with volunteer');
          setShowUploadDocDialog(false);
          setUploadDocTitle('');
          setUploadDocDescription('');
          setUploadDocFile(null);
          setSendDocNotification(true);
          fetchDocuments();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to upload document');
        }
        setUploadingDoc(false);
      };

      reader.readAsDataURL(uploadDocFile);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
      setUploadingDoc(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await fetch(`${API}/admin/documents/${doc.document_id}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const link = window.document.createElement('a');
        link.href = `data:${data.file_type};base64,${data.file_data}`;
        link.download = data.filename;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      } else {
        toast.error('Failed to download document');
      }
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleRemoveDocument = async (documentId) => {
    try {
      const response = await fetch(`${API}/admin/documents/${documentId}/unassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ volunteer_ids: [id] })
      });

      if (response.ok) {
        toast.success('Document removed from volunteer');
        fetchDocuments();
      } else {
        toast.error('Failed to remove document');
      }
    } catch (error) {
      toast.error('Failed to remove document');
    }
  };

  const getDocFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (fileType?.includes('spreadsheet') || fileType?.includes('excel') || fileType?.includes('csv')) 
      return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    if (fileType?.includes('word') || fileType?.includes('document')) 
      return <FileType className="w-5 h-5 text-blue-600" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  const formatDocFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!volunteer) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Volunteer not found</p>
          <Button onClick={() => navigate('/admin/volunteers')} className="mt-4">
            Back to Volunteers
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="volunteer-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/volunteers')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {volunteer.first_name} {volunteer.last_name}
              </h1>
              <p className="text-slate-500">{volunteer.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={() => window.open(`/volunteer-portal?vid=${encodeURIComponent(volunteer.volunteer_id)}&auto=true`, '_blank')}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid="view-portal-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Volunteer Portal
            </Button>
            <Button variant="destructive" onClick={deleteVolunteer} data-testid="delete-volunteer-btn">
              Delete Volunteer
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Status</span>
                <Select 
                  value={volunteer.status} 
                  onValueChange={(value) => updateVolunteer('status', value)}
                  disabled={updating}
                >
                  <SelectTrigger className="w-32" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Background Check</span>
                <Select 
                  value={volunteer.background_check_status} 
                  onValueChange={(value) => updateVolunteer('background_check_status', value)}
                  disabled={updating}
                >
                  <SelectTrigger className="w-32" data-testid="bg-check-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BG_CHECK_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Total Hours</span>
                <span className="text-2xl font-bold text-[#1a3a5c]">{volunteer.total_hours || 0}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Events Attended</span>
                <span className="text-2xl font-bold text-[#1a3a5c]">{volunteer.events_attended || 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Portal Access Info */}
        {volunteer.portal_accessed && (
          <Card className="border-0 shadow-md bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <LogIn className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">Portal Accessed</p>
                  <p className="text-xs text-green-600">
                    Last access: {volunteer.portal_last_accessed 
                      ? new Date(volunteer.portal_last_accessed).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })
                      : 'Unknown'
                    }
                    {volunteer.portal_access_count > 1 && ` (${volunteer.portal_access_count} times)`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="personal" className="space-y-4">
          <TabsList className="bg-white shadow-sm border">
            <TabsTrigger value="personal">Personal Info</TabsTrigger>
            <TabsTrigger value="roles">Roles & Skills</TabsTrigger>
            <TabsTrigger value="availability">Availability</TabsTrigger>
            <TabsTrigger value="motivation">Motivation</TabsTrigger>
            <TabsTrigger value="tasks" className="flex items-center gap-2">
              Tasks
              {volunteerTasks.length > 0 && (
                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                  {volunteerTasks.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex items-center gap-2">
              Emails
              {emailHistory.length > 0 && (
                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                  {emailHistory.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2">
              Documents
              {volunteerDocuments.length > 0 && (
                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                  {volunteerDocuments.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="personal">
            <Card className="border-0 shadow-md">
              <CardContent className="p-6 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#1a3a5c] flex items-center gap-2">
                      <User className="w-4 h-4" /> Contact Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {volunteer.email}
                      </div>
                      <div className="flex items-center gap-3 text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {volunteer.phone}
                      </div>
                      <div className="flex items-center gap-3 text-slate-600">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {volunteer.city}, {volunteer.state} {volunteer.zip_code}
                      </div>
                      <div className="flex items-center gap-3 text-slate-600">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        DOB: {volunteer.date_of_birth}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#1a3a5c] flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Emergency Contact
                    </h3>
                    {volunteer.emergency_contact && (
                      <div className="space-y-2 text-slate-600">
                        <p><strong>{volunteer.emergency_contact.name}</strong></p>
                        <p>{volunteer.emergency_contact.relationship}</p>
                        <p>{volunteer.emergency_contact.phone}</p>
                      </div>
                    )}
                  </div>
                </div>

                {volunteer.pronouns && (
                  <div className="pt-4 border-t">
                    <span className="text-sm text-slate-500">Pronouns: </span>
                    <span className="text-slate-600">{volunteer.pronouns}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="roles">
            <Card className="border-0 shadow-md">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1a3a5c] flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Volunteer Roles
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {volunteer.volunteer_roles?.length > 0 ? (
                      volunteer.volunteer_roles.map((role, i) => (
                        <Badge key={i} className="bg-[#5cc8e8]/10 text-[#1a3a5c] border-[#5cc8e8]">
                          {role}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-slate-500">No roles selected</span>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1a3a5c]">Professional Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {volunteer.professional_skills?.length > 0 ? (
                      volunteer.professional_skills.map((skill, i) => (
                        <Badge key={i} variant="outline">{skill}</Badge>
                      ))
                    ) : (
                      <span className="text-slate-500">No skills listed</span>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1a3a5c]">Languages Spoken</h3>
                  <div className="flex flex-wrap gap-2">
                    {volunteer.languages_spoken?.length > 0 ? (
                      volunteer.languages_spoken.map((lang, i) => (
                        <Badge key={i} variant="outline">{lang}</Badge>
                      ))
                    ) : (
                      <span className="text-slate-500">No languages listed</span>
                    )}
                  </div>
                </div>

                {volunteer.other_skills && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#1a3a5c]">Other Skills</h3>
                    <p className="text-slate-600">{volunteer.other_skills}</p>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <span className="text-sm text-slate-500">Experience with children: </span>
                  <Badge className={volunteer.experience_with_children ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                    {volunteer.experience_with_children ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="availability">
            <Card className="border-0 shadow-md">
              <CardContent className="p-6 space-y-6">
                {volunteer.availability && (
                  <>
                    <div className="space-y-4">
                      <h3 className="font-semibold text-[#1a3a5c] flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> Available Days
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {volunteer.availability.days_available?.map((day, i) => (
                          <Badge key={i} className="bg-[#1a3a5c] text-white">{day}</Badge>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="font-semibold text-[#1a3a5c] flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Preferred Times
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {volunteer.availability.time_preferences?.map((time, i) => (
                          <Badge key={i} variant="outline">{time}</Badge>
                        ))}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 pt-4 border-t">
                      <div>
                        <span className="text-sm text-slate-500">Hours per month: </span>
                        <span className="font-semibold text-[#1a3a5c]">{volunteer.availability.hours_per_month}</span>
                      </div>
                      <div>
                        <span className="text-sm text-slate-500">Commitment length: </span>
                        <span className="font-semibold text-[#1a3a5c]">{volunteer.availability.commitment_length}</span>
                      </div>
                    </div>
                  </>
                )}

                {volunteer.transportation && (
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold text-[#1a3a5c] mb-4">Transportation</h3>
                    <div className="grid md:grid-cols-2 gap-4 text-slate-600">
                      <div>
                        <span className="text-sm text-slate-500">Has transportation: </span>
                        <Badge className={volunteer.transportation.has_transportation ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {volunteer.transportation.has_transportation ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-sm text-slate-500">Can transport materials: </span>
                        <Badge className={volunteer.transportation.can_transport_materials ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                          {volunteer.transportation.can_transport_materials ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      {volunteer.transportation.travel_radius && (
                        <div>
                          <span className="text-sm text-slate-500">Travel radius: </span>
                          <span>{volunteer.transportation.travel_radius}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="motivation">
            <Card className="border-0 shadow-md">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1a3a5c]">Why do you want to volunteer?</h3>
                  <p className="text-slate-600">{volunteer.why_volunteer || 'Not provided'}</p>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1a3a5c]">What does literacy mean to you?</h3>
                  <p className="text-slate-600">{volunteer.literacy_meaning || 'Not provided'}</p>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1a3a5c]">What impact do you hope to make?</h3>
                  <p className="text-slate-600">{volunteer.impact_hope || 'Not provided'}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <div className="space-y-6">
              {/* Assign Task Section */}
              <Card className="border-0 shadow-md">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                    <ClipboardList className="w-5 h-5" /> Assign Task
                  </CardTitle>
                  <Button
                    onClick={openAssignTaskDialog}
                    className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                    data-testid="assign-task-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Assign Task
                  </Button>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500">
                    Assign tasks based on {volunteer.first_name}'s volunteer interests:
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {volunteer.volunteer_roles?.map((role, i) => (
                      <Badge key={i} className="bg-[#5cc8e8]/10 text-[#1a3a5c] border-[#5cc8e8]">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Assigned Tasks */}
              <Card className="border-0 shadow-md">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                    <ClipboardList className="w-5 h-5" /> Assigned Tasks ({volunteerTasks.length})
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchVolunteerTasks}
                    disabled={loadingTasks}
                    data-testid="refresh-tasks-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingTasks ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {volunteerTasks.length > 0 ? (
                    <div className="space-y-3">
                      {volunteerTasks.map((task, index) => (
                        <div
                          key={index}
                          className="p-4 bg-slate-50 rounded-lg border"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={getPriorityColor(task.priority)}>
                                  {task.priority}
                                </Badge>
                                <Badge className={getTaskStatusColor(task.status)}>
                                  {task.status}
                                </Badge>
                              </div>
                              <p className="font-medium text-[#1a3a5c]">{task.title}</p>
                              <p className="text-sm text-slate-500">
                                Interest: {task.related_interest}
                              </p>
                              {task.due_date && (
                                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                                  <Calendar className="w-3 h-3" />
                                  Due: {formatDate(task.due_date)}
                                </p>
                              )}
                            </div>
                            <div className="text-right text-xs text-slate-400">
                              Assigned: {formatDate(task.assigned_at)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No tasks assigned yet</p>
                      <p className="text-sm">Use the button above to assign tasks to this volunteer</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Emails Tab */}
          <TabsContent value="emails">
            <div className="space-y-6">
              {/* Send Email Section */}
              <Card className="border-0 shadow-md">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                    <Send className="w-5 h-5" /> Send Email
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 mb-4">
                    Choose a template to send an email to {volunteer.first_name}:
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {EMAIL_TEMPLATES.map((template) => (
                      <Button
                        key={template.id}
                        variant="outline"
                        className="h-auto py-4 flex flex-col items-start text-left hover:border-[#5cc8e8] hover:bg-[#5cc8e8]/5"
                        onClick={() => openEmailDialog(template)}
                        data-testid={`send-${template.id}-btn`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <template.icon className="w-4 h-4 text-[#1a3a5c]" />
                          <span className="font-medium">{template.name}</span>
                        </div>
                        <span className="text-xs text-slate-500">{template.description}</span>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Email History Section */}
              <Card className="border-0 shadow-md">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                    <Mail className="w-5 h-5" /> Email History
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchEmailHistory}
                    disabled={loadingEmails}
                    data-testid="refresh-emails-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingEmails ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {emailHistory.length > 0 ? (
                    <div className="space-y-3">
                      {emailHistory.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at)).map((email, index) => (
                        <div
                          key={index}
                          className="p-4 bg-slate-50 rounded-lg border"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge 
                                  className={
                                    email.email_type === 'approval' 
                                      ? 'bg-green-100 text-green-800' 
                                      : email.email_type === 'rejection'
                                      ? 'bg-red-100 text-red-800'
                                      : email.email_type === 'custom'
                                      ? 'bg-purple-100 text-purple-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }
                                >
                                  {formatEmailType(email.email_type)}
                                </Badge>
                                {email.manual && (
                                  <Badge variant="outline" className="text-xs">Manual</Badge>
                                )}
                              </div>
                              <p className="font-medium text-[#1a3a5c]">{email.subject}</p>
                              <p className="text-sm text-slate-500">To: {email.sent_to}</p>
                            </div>
                            <div className="text-right">
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {email.status}
                              </Badge>
                              <p className="text-xs text-slate-400 mt-1">{formatDate(email.sent_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No emails sent yet</p>
                      <p className="text-sm">Use the templates above to send an email to this volunteer</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="border-0 shadow-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-[#1a3a5c]">
                  Shared Documents
                </CardTitle>
                <Button
                  onClick={() => setShowUploadDocDialog(true)}
                  className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                  size="sm"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
              </CardHeader>
              <CardContent>
                {volunteerDocuments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No documents shared with this volunteer</p>
                    <p className="text-sm">Click "Upload Document" to share a file</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {volunteerDocuments.map((doc) => (
                      <div
                        key={doc.document_id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100"
                      >
                        <div className="flex items-center gap-3">
                          {getDocFileIcon(doc.file_type)}
                          <div>
                            <p className="font-medium text-[#1a3a5c]">{doc.title}</p>
                            <p className="text-xs text-slate-500">
                              {doc.filename} • {formatDocFileSize(doc.file_size)}
                            </p>
                            <p className="text-xs text-slate-400">
                              Shared on {new Date(doc.assigned_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadDocument(doc)}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveDocument(doc.document_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Remove from volunteer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Upload Document Dialog */}
        <Dialog open={showUploadDocDialog} onOpenChange={setShowUploadDocDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Document for {volunteer?.first_name}</DialogTitle>
              <DialogDescription>
                This document will be shared directly with this volunteer.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>File *</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  {uploadDocFile ? (
                    <div className="flex items-center justify-center gap-3">
                      {getDocFileIcon(uploadDocFile.type)}
                      <div className="text-left">
                        <p className="font-medium">{uploadDocFile.name}</p>
                        <p className="text-xs text-slate-500">{formatDocFileSize(uploadDocFile.size)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUploadDocFile(null)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500 mb-2">
                        PDF, Word, Excel, CSV (max 10MB)
                      </p>
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv"
                        onChange={handleDocFileSelect}
                        className="hidden"
                        id="vol-doc-upload"
                      />
                      <Button
                        variant="outline"
                        onClick={() => window.document.getElementById('vol-doc-upload').click()}
                      >
                        Choose File
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={uploadDocTitle}
                  onChange={(e) => setUploadDocTitle(e.target.value)}
                  placeholder="Document title"
                />
              </div>
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Textarea
                  value={uploadDocDescription}
                  onChange={(e) => setUploadDocDescription(e.target.value)}
                  placeholder="Brief description..."
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send-doc-notification"
                  checked={sendDocNotification}
                  onChange={(e) => setSendDocNotification(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="send-doc-notification" className="text-sm cursor-pointer">
                  Send email notification to volunteer
                </Label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUploadDocDialog(false);
                    setUploadDocTitle('');
                    setUploadDocDescription('');
                    setUploadDocFile(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUploadDocument}
                  disabled={uploadingDoc || !uploadDocFile || !uploadDocTitle}
                  className="flex-1 bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                >
                  {uploadingDoc ? 'Uploading...' : 'Upload & Share'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Email Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedTemplate && <selectedTemplate.icon className="w-5 h-5 text-[#1a3a5c]" />}
                Send {selectedTemplate?.name}
              </DialogTitle>
              <DialogDescription>
                This email will be sent to {volunteer?.email}
              </DialogDescription>
            </DialogHeader>
            
            {selectedTemplate && (
              <div className="space-y-4 pt-4">
                {selectedTemplate.requiresReason && (
                  <div className="space-y-2">
                    <Label htmlFor="rejection-reason">Rejection Reason</Label>
                    <Textarea
                      id="rejection-reason"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Please provide a reason for the rejection..."
                      rows={3}
                      data-testid="rejection-reason-input"
                    />
                  </div>
                )}

                {selectedTemplate.requiresCustom && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="custom-subject">Subject</Label>
                      <Input
                        id="custom-subject"
                        value={customSubject}
                        onChange={(e) => setCustomSubject(e.target.value)}
                        placeholder="Email subject..."
                        data-testid="custom-subject-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="custom-message">Message</Label>
                      <Textarea
                        id="custom-message"
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        placeholder="Write your message..."
                        rows={6}
                        data-testid="custom-message-input"
                      />
                    </div>
                  </>
                )}

                {!selectedTemplate.requiresReason && !selectedTemplate.requiresCustom && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">
                      The standard <strong>{selectedTemplate.name}</strong> template will be sent to {volunteer?.first_name}.
                    </p>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowEmailDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendEmail}
                    disabled={sendingEmail}
                    className="flex-1 bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                    data-testid="confirm-send-email-btn"
                  >
                    {sendingEmail ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Assign Task Dialog */}
        <Dialog open={showAssignTaskDialog} onOpenChange={setShowAssignTaskDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#1a3a5c]" />
                Assign Task to {volunteer?.first_name}
              </DialogTitle>
              <DialogDescription>
                Select a task that matches their interests
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {availableTasks.length > 0 ? (
                <>
                  <div className="space-y-2">
                    <Label>Select Task</Label>
                    <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
                      <SelectTrigger data-testid="task-select">
                        <SelectValue placeholder="Choose a task..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTasks.map(task => (
                          <SelectItem key={task.task_id} value={task.task_id}>
                            <div className="flex flex-col">
                              <span>{task.title}</span>
                              <span className="text-xs text-slate-500">{task.related_interest}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Custom Message (optional)</Label>
                    <Textarea
                      value={taskCustomMessage}
                      onChange={(e) => setTaskCustomMessage(e.target.value)}
                      placeholder="Add a personal note to the notification email..."
                      rows={3}
                      data-testid="task-message-input"
                    />
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowAssignTaskDialog(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={assignTaskToVolunteer}
                      disabled={assigningTask || !selectedTaskId}
                      className="flex-1 bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                      data-testid="confirm-assign-task-btn"
                    >
                      {assigningTask ? 'Assigning...' : 'Assign Task'}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p>No matching open tasks available.</p>
                  <p className="text-sm mt-1">
                    Create a new task in the <a href="/admin/tasks" className="text-[#5cc8e8] hover:underline">Tasks section</a> first.
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
